import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateCustomerPdfCelebrityRequest,
  CreateCustomerPdfCelebrityRequestSchema,
  type CustomerPdfCelebrity,
  type UpdateCustomerPdfCelebrityRequest,
  UpdateCustomerPdfCelebrityRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateCelebrity, useUpdateCelebrity } from '../use-customer-pdf';

import { MediaDropField } from '@/features/website-cms/components/MediaDropField';

interface CelebrityFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  celebrity?: CustomerPdfCelebrity | null;
}

/**
 * Create/edit drawer for a "A Legacy of Trust & Excellence" celebrity card:
 * portrait photo, name, one-line bio and the sort order that decides which
 * six make it onto the customer page.
 */
export const CelebrityFormDrawer = ({
  open,
  onOpenChange,
  mode,
  celebrity,
}: CelebrityFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createMut = useCreateCelebrity();
  const updateMut = useUpdateCelebrity();
  const pending = createMut.isPending || updateMut.isPending;

  const createForm = useForm<CreateCustomerPdfCelebrityRequest>({
    resolver: zodResolver(CreateCustomerPdfCelebrityRequestSchema),
    defaultValues: { name: '', description: '', imageId: null, sortOrder: 0 },
  });
  const updateForm = useForm<UpdateCustomerPdfCelebrityRequest>({
    resolver: zodResolver(UpdateCustomerPdfCelebrityRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset({ name: '', description: '', imageId: null, sortOrder: 0 });
    } else if (celebrity) {
      updateForm.reset({
        name: celebrity.name,
        description: celebrity.description,
        imageId: celebrity.imageId,
        sortOrder: celebrity.sortOrder,
      });
    }
  }, [open, isCreate, celebrity, createForm, updateForm]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = (isCreate ? createForm : updateForm) as ReturnType<typeof useForm<any>>;

  const onCreate = createForm.handleSubmit(async (values) => {
    try {
      await createMut.mutateAsync(values);
      onOpenChange(false);
    } catch {
      /* toast already fired in mutation hook */
    }
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!celebrity) return;
    try {
      await updateMut.mutateAsync({ id: celebrity.id, payload: values });
      onOpenChange(false);
    } catch {
      /* toast already fired */
    }
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add celebrity' : `Edit ${celebrity?.name ?? 'celebrity'}`}
      description='Shown in the "A Legacy of Trust & Excellence" section of the customer page. Only the top 6 by sort order appear.'
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Add celebrity' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={isCreate ? onCreate : onUpdate}>
        <UzFormField name="imageId">
          {({ field }) => (
            <MediaDropField
              value={field.value ?? null}
              onChange={(id) => field.onChange(id)}
              label="Photo"
              folder="customer-pdf"
              variant="square"
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="name">
          {({ field, error }) => (
            <UzInput
              {...field}
              label="Name"
              placeholder="Pramod Pathak"
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="description">
          {({ field, error }) => (
            <UzTextarea
              {...field}
              label="Short description"
              placeholder="Indian actor featured in Gangs of Wasseypur, Mirzapur, and Maharani. Travelled with Utazzo Holidays - Stress-Free, Always."
              rows={4}
              maxLength={500}
              showCount
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="sortOrder">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              label="Sort order"
              value={field.value ?? 0}
              onValueChange={(v) => field.onChange(v ?? 0)}
              min={0}
              max={9999}
              step={10}
              hint="Lower values appear first. Only the first 6 show on the customer page."
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </UzForm>
    </UzDrawer>
  );
};
